// Call plugin.
import { callActions as actions } from '../actions'

// Libraries.
import { v4 as uuidV4 } from 'uuid'
import { isEmpty, pick } from 'lodash/fp'

/**
 * Anonymous Call API
 * Returns a function that returns an Altered API that it is suited only for anonymous calls.
 * @method anonymousAPI
 * @param {function} otherApi The api to alter
 * @return {Object} Anonymous Call plugin API.
 */
export default function anonymousAPI (otherApi) {
  return function newCallMeApi ({ dispatch, getState }) {
    // List of APIs that this plugin will take from the interface.
    //      APIs not included here will not be exposed.
    // The difference between regular calls and callMe anonymous calls is that:
    //      1. anon is outgoing only,
    //      2. anon is basic mid-call operations only (eg. no transfer),
    //      3. anon's makeCall is not called directly (after login).
    const anonAPIs = [
      // Getters.
      'getAll',
      'getById',
      'getAvailableCodecs',
      // Setters.
      'setCustomParameters',
      'setSdpHandlers',
      // Call functionality.
      'hold',
      'unhold',
      'sendCustomParameters',
      'end',
      'addMedia',
      'removeMedia',
      'startVideo',
      'stopVideo',
      'sendDTMF',
      'getStats',
      'replaceTrack',
      'restartMedia',
      // States,
      'states',
      'mediaConnectionStates'
    ]

    const newLinkAPI = pick(anonAPIs, otherApi({ dispatch, getState }).call)

    return {
      call: {
        ...newLinkAPI,
        /**
         * Starts an outgoing call as an anonymous user.
         * @public
         * @requires callMe
         * @memberof call
         * @method makeAnonymous
         * @param {string} callee       Full user ID of the call recipient.
         * @param {Object} credentials  Information needed to validate a token anonymous call.
         * @param {Object} credentials.realm The realm used to encrypt the tokens.
         * @param {Object} [credentials.accountToken] The encrypted account token of the account making the call.
         * @param {Object} [credentials.fromToken] The encrypted SIP address of the account/caller.
         * @param {Object} [credentials.toToken] The encrypted SIP address of the callee.
         * @param {Object} callOptions Call options.
         * @param {string} callOptions.from The URI of the user making the call.
         * @param {Boolean} [callOptions.audio=true] Whether the call should have audio on start. Currently, audio-less calls are not supported.
         * @param {Object} [callOptions.audioOptions] Options for configuring the call's audio.
         * @param {call.BandwidthControls} [callOptions.bandwidth] Options for configuring media's bandwidth.
         * @param {call.MediaConstraint} [callOptions.audioOptions.deviceId] ID of the microphone to receive audio from.
         * @param {Boolean} [callOptions.video=false] Whether the call should have video on start.
         * @param {Object} [callOptions.videoOptions] Options for configuring the call's video.
         * @param {call.MediaConstraint} [callOptions.videoOptions.deviceId] ID of the camera to receive video from.
         * @param {call.MediaConstraint} [callOptions.videoOptions.height] The height of the video.
         * @param {call.MediaConstraint} [callOptions.videoOptions.width] The width of the video.
         * @param {call.MediaConstraint} [callOptions.videoOptions.frameRate] The frame rate of the video.
         * @param {Boolean} [callOptions.screen=false] Whether the call should have screenshare on start.
         * @param {Object} [callOptions.screenOptions] Options for configuring the call's screenShare.
         * @param {call.MediaConstraint} [callOptions.screenOptions.height] The height of the screenShare.
         * @param {call.MediaConstraint} [callOptions.screenOptions.width] The width of the screenShare.
         * @param {call.MediaConstraint} [callOptions.screenOptions.frameRate] The frame rate of the screenShare.
         * @param {string} [callOptions.displayName] Custom display name to be provided to the destination. Only used with token-less anonymous calls. Not supported in all environments and may use default display name.
         * @param {Array<call.CustomParameter>} [callOptions.customParameters] Custom SIP header parameters for the SIP backend.
         * @return {string} Id of the outgoing call.
         * @example
         * // Make a basic anonymous call.
         * let callee = 'user1@example.com';
         * let callOptions = { ... };
         *
         * let callId = client.call.makeAnonymous(callee, {}, callOptions);
         * @example
         * // Make a time-limited token anonymous call.
         * let callee = 'user1@example.com';
         * let account = 'user2@example.com';
         * let callOptions = { ...
         *     customParameters: [
         *       {
         *         "name": "X-GPS",
         *         "value": "42.686032,23.344565"
         *       }
         *     ],
         *     ...
         *   };
         *
         * // Generate / Retrieve the encrypted tokens.
         * const key = 'abc123...';
         * const credentials = {
         *      accountToken: createToken(account, key),
         *      fromToken: createToken('sip:' + account, key),
         *      toToken: createToken('sip:' + callee, key),
         *      realm: 'realmAbc123...'
         * };
         *
         * let callId = client.call.makeAnonymous(callee, credentials, callOptions);
         */

        /**
         * @private
         * @memberof call
         * @requires callMe
         * @method makeAnonymous
         * @param {Object} callOptions Call options.
         * @param {Array<call.CustomBody>} [callOptions.customBodies] Custom SIP bodies for the SIP backend.
         */
        makeAnonymous (callee, credentials = {}, callOptions = {}) {
          // Separate the media constraints from the callOptions.
          const { audio, audioOptions, video, videoOptions, screen, screenOptions, ...options } = callOptions

          // Massage the media constraints into a gUM-like format.
          //    This is done the same way as the make API does it.
          const mediaConstraints = {
            audio: audio && !isEmpty(audioOptions) ? audioOptions : audio,
            video: video && !isEmpty(videoOptions) ? videoOptions : video,
            screenShare: screen && !isEmpty(screenOptions) ? screenOptions : screen
          }

          // Create our own call ID for storing in state.
          const callId = uuidV4()
          dispatch(
            actions.makeAnonymousCall(callId, {
              callee,
              credentials,
              mediaConstraints,
              callOptions: options
            })
          )
          return callId
        }
      }
    }
  }
}
